import { CheckboxV2 } from "#src/commonV2/inputs/Checkbox";
import { TableDataCellV2 } from "../../components/TableDataCell";
import { TableRowWrapperV2 } from "../../components/TableRowWrapper";
import { HTMLAttributes, ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { ChevronSelectorVertical } from "@untitled-ui/icons-react";
import { CSS } from "@dnd-kit/utilities";

interface ITableDndRowProps<DataType> {
  item: DataType;
  rowIndex: number;
  getRowKey: (dataItem: DataType) => string;
  columns: {
    title: ReactNode;
    /** Theoretically we should let developers overwrite everything, but I've yet to find any cases we need more than this alignment. */
    titleAlign?: "left" | "center" | "right";
    render: (dataItem: DataType, index: number) => ReactNode;
    /** Literally let you overwrite any props of cell wrapper. */
    overwriteWrapperProps?: HTMLAttributes<HTMLDivElement>;
    gridTemplateValue?: string;
  }[];
  rowSelection?: {
    selectedRowKeys: string[];
    onChange: (newSelectedKeys: string[]) => void;
  };
  onClickRow?: (item: DataType) => void;
  isLastRow?: boolean;
}

export const TableDndRow = <DataType,>({
  item,
  rowIndex,
  getRowKey,
  columns,
  rowSelection,
  onClickRow,
  isLastRow = false,
}: ITableDndRowProps<DataType>) => {
  // For setting React key and checking selected rows
  const rowKey = getRowKey(item);

  const {
    attributes,
    listeners,
    isDragging,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: rowKey });

  const style = {
    opacity: isDragging ? 0 : undefined,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    // This div is for the expand function. Supposedly we only need the TableRowWrapper.
    <div
      ref={setNodeRef}
      style={style}
      className={`grid grid-cols-subgrid col-span-full ${isLastRow ? "" : "border-b border-gray-v2-200"}`}
    >
      <TableRowWrapperV2
        className={`${
          rowSelection && rowSelection.selectedRowKeys.includes(rowKey)
            ? "bg-gray-v2-50"
            : ""
        } ${onClickRow ? "cursor-pointer" : ""}`}
        onClick={() => {
          if (onClickRow) {
            onClickRow(item);
          }
        }}
      >
        <TableDataCellV2
          className="justify-center"
          {...attributes}
          {...listeners}
        >
          <ChevronSelectorVertical className="h-4 w-4 min-w-4" />
        </TableDataCellV2>
        {rowSelection ? (
          <TableDataCellV2 className="justify-center">
            <CheckboxV2
              checked={rowSelection.selectedRowKeys.includes(rowKey)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (rowSelection.selectedRowKeys.includes(rowKey)) {
                  rowSelection.onChange(
                    rowSelection.selectedRowKeys.filter((k) => k != rowKey)
                  );
                } else {
                  rowSelection.onChange([
                    ...rowSelection.selectedRowKeys,
                    rowKey,
                  ]);
                }
              }}
            />
          </TableDataCellV2>
        ) : null}
        {columns.map((column, colIndex) => (
          <TableDataCellV2
            key={`table-cell-${rowKey}-${colIndex}`}
            {...column.overwriteWrapperProps}
          >
            {column.render(item, rowIndex)}
          </TableDataCellV2>
        ))}
      </TableRowWrapperV2>
    </div>
  );
};
